import { CheckCircle2, Loader2, Radar, X, XCircle } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { api, adminHeaders } from "@/lib/api";

// Monitor da "Descobrir Polémicas". A corrida vive no servidor, por isso um F5
// não a cancela: o painel só volta a perguntar o estado. Um reinício do
// servidor (deploy) interrompe-a, e o monitor diz isso.
const POLL_RUNNING_MS = 4000;
const RECENT_MS = 24 * 60 * 60 * 1000;
const DISMISS_KEY = "vo-auto-discover-dismissed";

const STATUS_LABELS = {
        created: "criado",
        skipped: "ignorado",
        failed: "falhou",
        blocked: "bloqueado",
};

const readDismissed = () => {
        try {
                return window.localStorage.getItem(DISMISS_KEY) || "";
        } catch {
                return "";
        }
};

const formatTime = (value) => {
        if (!value) return "";
        const date = new Date(value);
        return Number.isNaN(date.getTime())
                ? ""
                : new Intl.DateTimeFormat("pt-PT", { hour: "2-digit", minute: "2-digit", second: "2-digit" }).format(date);
};

const elapsed = (from) => {
        const start = new Date(from).getTime();
        if (!start) return "";
        const seconds = Math.max(0, Math.round((Date.now() - start) / 1000));
        return seconds < 60 ? `${seconds} s` : `${Math.floor(seconds / 60)} min ${seconds % 60} s`;
};

export const AutoDiscoverMonitor = ({ refreshKey, onFinished }) => {
        const [state, setState] = useState(null);
        const [dismissed, setDismissed] = useState(readDismissed);
        const wasRunning = useRef(false);

        const load = useCallback(async () => {
                try {
                        const { data } = await api.get("/admin/auto-discover/status", { headers: adminHeaders() });
                        setState(data);
                        if (wasRunning.current && !data.running) onFinished?.();
                        wasRunning.current = Boolean(data.running);
                } catch {
                        // Sem estado (rede ou sessão): o monitor fica como estava.
                }
        }, [onFinished]);

        useEffect(() => {
                load();
        }, [load, refreshKey]);

        useEffect(() => {
                if (!state?.running) return undefined;
                const timer = window.setTimeout(load, POLL_RUNNING_MS);
                return () => window.clearTimeout(timer);
        }, [state, load]);

        if (!state) return null;

        const running = Boolean(state.running);
        const lastJob = state.last_job;
        const result = running ? state.current || {} : state.last_result || lastJob?.result || {};
        const finishedAt = state.finished_at || lastJob?.finished_at || lastJob?.started_at;
        const error = state.last_error || result.trend_selection_error || result.error || lastJob?.error;
        const interrupted = lastJob?.status === "interrupted";
        const failed = Boolean(error) && !result.created;
        const recent = finishedAt && Date.now() - new Date(finishedAt).getTime() < RECENT_MS;

        if (!running && (!recent || dismissed === String(finishedAt))) return null;

        const dismiss = () => {
                try {
                        window.localStorage.setItem(DISMISS_KEY, String(finishedAt));
                } catch {
                        // Sem armazenamento: some só até ao próximo F5.
                }
                setDismissed(String(finishedAt));
        };

        const processed = Array.isArray(result.processed) ? result.processed : [];
        const events = Array.isArray(state.events) ? state.events.slice(-6).reverse() : [];

        return (
                <section className="adm-card mb-6 p-4" data-testid="auto-discover-monitor" aria-live="polite">
                        <div className="flex items-start gap-3">
                                {running ? (
                                        <Loader2 size={18} className="mt-0.5 animate-spin text-[var(--ai)]" />
                                ) : failed || interrupted ? (
                                        <XCircle size={18} className="mt-0.5 text-[var(--deception)]" />
                                ) : (
                                        <CheckCircle2 size={18} className="mt-0.5 text-[var(--truth)]" />
                                )}
                                <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-2">
                                                <Radar size={14} strokeWidth={1.75} />
                                                <span className="text-sm font-semibold text-[var(--ink)]">
                                                        {running
                                                                ? `Descoberta de polémicas a correr há ${elapsed(state.started_at)}`
                                                                : interrupted
                                                                ? "A última descoberta foi interrompida"
                                                                : `Última descoberta terminou às ${formatTime(finishedAt)}`}
                                                </span>
                                                <span className="text-xs text-[var(--ink-faint)]">
                                                        {[
                                                                `${result.created || 0} criado(s)`,
                                                                `${result.skipped || 0} ignorado(s)`,
                                                                `${result.failed || 0} falhado(s)`,
                                                        ].join(" · ")}
                                                </span>
                                        </div>
                                        {running && state.stage && <p className="mt-1 text-sm text-[var(--ink-soft)]">{state.stage}</p>}
                                        {!running && state.stage && !interrupted && <p className="mt-1 text-sm text-[var(--ink-soft)]">{state.stage}</p>}
                                        {!running && error && <p className="mt-1 text-sm text-[var(--deception)]">{String(error).slice(0, 400)}</p>}
                                        {running && (
                                                <p className="mt-1 text-xs text-[var(--ink-faint)]">
                                                        Corre no servidor: podes atualizar a página (F5) ou fechar o separador sem a cancelar.
                                                </p>
                                        )}
                                        {processed.length > 0 && (
                                                <ul className="mt-2 space-y-1 text-xs">
                                                        {processed.slice(-8).map((item, index) => (
                                                                <li key={`${item.query}-${index}`} className="text-[var(--ink-soft)]">
                                                                        <strong>{STATUS_LABELS[item.status] || item.status}:</strong> {item.query || "(sem consulta)"}
                                                                        {item.reason ? <span className="text-[var(--ink-faint)]"> — {String(item.reason).slice(0, 200)}</span> : null}
                                                                </li>
                                                        ))}
                                                </ul>
                                        )}
                                        {events.length > 1 && (
                                                <details className="mt-2">
                                                        <summary className="cursor-pointer text-xs text-[var(--ink-faint)]">Etapas</summary>
                                                        <ul className="mt-1 space-y-0.5 font-mono text-[11px] text-[var(--ink-faint)]">
                                                                {events.map((event, index) => (
                                                                        <li key={`${event.at}-${index}`}>
                                                                                {formatTime(event.at)} · {event.message}
                                                                        </li>
                                                                ))}
                                                        </ul>
                                                </details>
                                        )}
                                </div>
                                {!running && (
                                        <button type="button" onClick={dismiss} className="text-[var(--ink-faint)]" aria-label="Fechar aviso da descoberta">
                                                <X size={16} />
                                        </button>
                                )}
                        </div>
                </section>
        );
};
